import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bot, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { EvidenceChip, SourceTypeBadge } from "@/components/ripeness/SourceBadges";

const KIND_META = {
    base: { label: "Start", tone: "border-sky-400/40 text-sky-300" },
    adjust: { label: "Adjust", tone: "border-amber-400/40 text-amber-300" },
    cap: { label: "Cap", tone: "border-rose-400/40 text-rose-300" },
    note: { label: "Note", tone: "border-slate-600 text-slate-400" },
};
const SRC_LABEL = { "ct.gov": "ClinicalTrials.gov", ctis: "CTIS (EU)", inferred: "Inferred", "ai-signal": "News / filing signal" };

function Step({ s, phase, i }) {
    const k = KIND_META[s.kind] || KIND_META.note;
    return (
        <li className="flex gap-3 py-1.5 border-t border-slate-800/70 first:border-t-0" data-testid={`why-step-${phase}-${i}`}>
            <span className={`shrink-0 h-fit rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${k.tone}`}>{k.label}</span>
            <div className="flex-1 min-w-0">
                <div className="text-xs text-slate-200 leading-snug">{s.text}</div>
                {s.evidence?.length ? (
                    <div className="flex flex-wrap gap-1 mt-1">
                        {s.evidence.map((e, j) => <EvidenceChip key={`${e.id}-${j}`} ev={e} testId={`why-ev-${phase}-${i}-${j}`} />)}
                    </div>
                ) : null}
            </div>
            <div className="shrink-0 text-right w-14">
                <div className="text-sm font-semibold text-slate-100" data-testid={`why-step-score-${phase}-${i}`}>{s.score}</div>
                {s.delta ? <div className={`text-[10px] ${s.delta > 0 ? "text-emerald-300" : "text-rose-300"}`}>{s.delta > 0 ? "+" : ""}{s.delta}</div> : null}
            </div>
        </li>
    );
}

function PhaseSteps({ phase, node, steps, best }) {
    return (
        <div className={`rounded-lg border ${best ? "border-emerald-400/40" : "border-slate-800"} bg-slate-900/50 p-3`} data-testid={`why-phase-${phase}`}>
            <div className="flex items-center justify-between mb-1">
                <div className="text-sm font-semibold text-slate-100">Phase {phase}{best ? <span className="ml-2 text-[10px] text-emerald-300 uppercase tracking-wider">sets ripeness</span> : null}</div>
                <div className="text-xs text-slate-400">{SRC_LABEL[node?.source] || node?.source || "—"} · <b className="text-slate-100">{node?.score ?? "—"}</b></div>
            </div>
            <ol>{(steps || []).map((s, i) => <Step key={i} s={s} phase={phase} i={i} />)}</ol>
        </div>
    );
}

function Outcome({ ex }) {
    if (!ex) return null;
    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 text-xs" data-testid="why-outcome">
            <div className="rounded border border-slate-800 p-2" data-testid="why-best-phase"><div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Ripeness</div>{ex.best_phase?.text}</div>
            <div className="rounded border border-slate-800 p-2" data-testid="why-tier"><div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Tier</div>{ex.tier?.text}</div>
            <div className="rounded border border-slate-800 p-2" data-testid="why-priority">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Priority {ex.priority?.total}</div>
                {(ex.priority?.parts || []).map((p, i) => (
                    <div key={i} className="flex justify-between gap-2" data-testid={`why-priority-part-${i}`}>
                        <span className="text-slate-300">{p.text}</span><span className={p.points < 0 ? "text-rose-300" : "text-emerald-300"}>{p.points > 0 ? "+" : ""}{p.points}</span>
                    </div>
                ))}
                <div className="text-slate-500 mt-1">{ex.priority?.text}</div>
            </div>
        </div>
    );
}

function AiSummary({ summary }) {
    if (!summary) return null;
    return (
        <div className="rounded-lg border border-sky-400/30 bg-sky-400/5 p-3 mb-3" data-testid="why-ai-summary">
            <div className="text-[10px] uppercase tracking-wider text-sky-300 mb-1 inline-flex items-center gap-1"><Bot className="w-3 h-3" /> AI summary</div>
            {summary.text
                ? <p className="text-sm text-slate-200 leading-relaxed" data-testid="why-ai-summary-text">{summary.text}</p>
                : <p className="text-xs text-amber-300" data-testid="why-ai-summary-error">Summary not available: {summary.error || "unknown error"}</p>}
        </div>
    );
}

function SourceCounts({ counts }) {
    const e = Object.entries(counts || {}).filter(([, n]) => n);
    if (!e.length) return null;
    return (
        <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="why-source-counts">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Evidence behind this score</span>
            {e.map(([t, n]) => <span key={t} className="inline-flex items-center gap-1" data-testid={`why-count-${t}`}><SourceTypeBadge type={t} /><span className="text-[11px] text-slate-400">{n}</span></span>)}
        </div>
    );
}

const DONE = ["done", "failed", "cancelled"];

function useProgramCheck(programId, onFinished) {
    const [run, setRun] = useState(null);
    const timer = useRef(null);
    useEffect(() => () => clearTimeout(timer.current), []);
    const poll = useCallback(async (jobId, tries = 0) => {
        try {
            const r = await api.get(`/v1/ripeness/external/runs/${jobId}`);
            setRun(r.data.run);
            if (DONE.includes(r.data.status)) {
                if (r.data.status === "done") toast.success(`New sources checked — ${r.data.run.results?.tracks_changed ?? 0} track(s) changed`);
                else toast.error(`Source check ${r.data.status}`);
                onFinished?.();
                return;
            }
        } catch (e) { /* keep polling */ }
        if (tries < 90) timer.current = setTimeout(() => poll(jobId, tries + 1), 3000);
    }, [onFinished]);
    const start = useCallback(async () => {
        try {
            const r = await api.post(`/v1/ripeness/programs/${encodeURIComponent(programId)}/external-refresh`);
            setRun(r.data.run);
            toast.info("Checking SEC, press releases, pipeline page and CTIS for this compound…");
            poll(r.data.run.job_id);
        } catch (e) {
            toast.error(`Source check failed: ${formatApiErrorDetail(e)}`);
        }
    }, [programId, poll]);
    return { run, start, busy: !!run && !DONE.includes(run.status) };
}

/** "Why this score" — AI summary, per-phase steps with evidence, tier + priority. */
export function ScoreExplanation({ trackId, programId, onRescored }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const load = useCallback(async () => {
        setErr(null);
        try {
            const r = await api.get(`/v1/ripeness/tracks/${encodeURIComponent(trackId)}/explanation`, { params: { summary: true } });
            setData(r.data);
        } catch (e) { setErr(formatApiErrorDetail(e)); }
    }, [trackId]);
    useEffect(() => { load(); }, [load]);
    const finished = useCallback(() => { load(); onRescored?.(); }, [load, onRescored]);
    const check = useProgramCheck(programId, finished);
    const ex = data?.explanation;
    const phases = Object.keys(data?.phases || {}).sort();
    return (
        <div className="mt-3 rounded-lg border border-slate-700/60 bg-slate-950/40 p-3" data-testid="why-panel">
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div className="text-xs uppercase tracking-wider text-slate-400">Why this score</div>
                {programId ? (
                    <button onClick={check.start} disabled={check.busy}
                        className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800/40 px-2 py-1 text-xs text-slate-200 hover:bg-slate-800 transition-colors disabled:opacity-50"
                        title="Fetch SEC filings, 10-Ks, press releases, the sponsor pipeline page and CTIS for this compound and re-score it now" data-testid="why-check-sources-btn">
                        {check.busy ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                        {check.busy ? `Checking… ${check.run?.progress?.tracks_evaluated || 0} tracks` : "Check new sources now"}
                    </button>
                ) : null}
            </div>
            {err ? <div className="text-xs text-rose-300" data-testid="why-error">{err}</div> : null}
            {!data && !err ? <div className="text-xs text-slate-500 inline-flex items-center gap-1.5" data-testid="why-loading"><RefreshCw className="w-3 h-3 animate-spin" /> Loading explanation and AI summary…</div> : null}
            {data && !data.available ? (
                <div className="text-xs text-amber-200" data-testid="why-unavailable">This score was calculated before step-by-step explanations existed. It appears after the next recheck — or use “Check new sources now”.</div>
            ) : null}
            {data?.available ? (
                <>
                    <AiSummary summary={data.summary} />
                    <SourceCounts counts={data.source_counts} />
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                        {phases.map((p) => <PhaseSteps key={p} phase={p} node={data.phases[p]} steps={ex?.phases?.[p]} best={String(p) === String(data.best_phase)} />)}
                    </div>
                    <Outcome ex={ex} />
                </>
            ) : null}
        </div>
    );
}
